import { useMemo } from "react";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import KPIGrid from "@/components/dashboard/KPIGrid";
import InvoiceRevenueChart from "@/components/dashboard/charts/InvoiceRevenueChart";
import TopCustomersChart from "@/components/dashboard/charts/TopCustomersChart";
import useDashboardData from "@/hooks/useDashboardData";
import { Card, CardContent } from "@/components/ui/card";

const formatCount = (value) =>
  new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 0,
  }).format(value || 0);

export default function DashboardInvoices() {
  const {
    overview,
    overviewLoading,
    overviewError,
    revenueSeries,
    chartsLoading,
    chartsError,
    rangeMode,
  } = useDashboardData({
    includeStatus: true,
    includeForecast: false,
    timeseriesSource: "invoice",
    timeseriesBreakdown: "status",
    overviewSource: "invoice",
  });

  const statusTotals = useMemo(() => {
    const totals = {};
    (revenueSeries || []).forEach((point) => {
      const breakdown = point.status_breakdown || {};
      Object.entries(breakdown).forEach(([status, values]) => {
        const net = values?.net || 0;
        const vat = values?.vat || 0;
        totals[status] = (totals[status] || 0) + net + vat;
      });
    });
    return totals;
  }, [revenueSeries]);

  const openAmount = (statusTotals.sent || 0) + (statusTotals.created || 0);
  const paidAmount = statusTotals.paid || 0;
  const openCount = overview?.open_invoices?.count || 0;
  const overdueCount = overview?.open_invoices?.overdue || 0;

  const kpiItems = useMemo(() => {
    return [
      {
        key: "invoice-paid-amount",
        title: "Bezahlt (Betrag)",
        value: paidAmount,
        testId: "invoice-kpi-paid-amount",
      },
      {
        key: "invoice-open-amount",
        title: "Offen (Betrag)",
        value: openAmount,
        testId: "invoice-kpi-open-amount",
      },
      {
        key: "invoice-open-count",
        title: "Offen (Anzahl)",
        value: openCount,
        valueFormatter: formatCount,
        testId: "invoice-kpi-open-count",
      },
      {
        key: "invoice-overdue-count",
        title: "Überfällig (Anzahl)",
        value: overdueCount,
        valueFormatter: formatCount,
        testId: "invoice-kpi-overdue-count",
      },
    ];
  }, [openAmount, openCount, overdueCount, paidAmount]);

  const revenueChartData = useMemo(() => {
    return (revenueSeries || []).map((point) => {
      const breakdown = point.status_breakdown || {};
      const row = {
        date: point.date,
        statusBreakdown: breakdown,
      };
      Object.entries(breakdown).forEach(([status, values]) => {
        row[status] = values?.net || 0;
      });
      return row;
    });
  }, [revenueSeries]);

  const showHistorical = rangeMode !== "future";

  return (
    <div className="space-y-6" data-testid="dashboard-invoices-view">
      <DashboardFilters
        title="Rechnungs-Filter"
        subtitle="Status & Umsatz deiner Rechnungen im Blick"
        showStatus
        showProjects
        showCustomers
      />

      <section data-testid="dashboard-invoices-kpis">
        {!showHistorical && (
          <Card>
            <CardContent className="p-6 text-sm text-slate-600" data-testid="dashboard-no-data">
              Für reine Zukunftszeiträume stehen keine historischen Rechnungsdaten zur Verfügung.
            </CardContent>
          </Card>
        )}
        {overviewLoading && (
          <Card>
            <CardContent className="p-6 text-sm text-slate-600" data-testid="invoice-kpi-loading">
              KPI-Daten werden geladen...
            </CardContent>
          </Card>
        )}
        {!overviewLoading && overviewError && (
          <Card>
            <CardContent className="p-6 text-sm text-red-600" data-testid="kpi-error">
              KPI-Daten konnten nicht geladen werden.
            </CardContent>
          </Card>
        )}
        {showHistorical && !overviewLoading && !overviewError && kpiItems.length > 0 && (
          <KPIGrid items={kpiItems} />
        )}
      </section>

      {showHistorical && (
        <section data-testid="dashboard-invoices-charts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <InvoiceRevenueChart data={revenueChartData} isLoading={chartsLoading} error={chartsError} />
            <TopCustomersChart
              data={overview?.top_customers || []}
              isLoading={overviewLoading}
              error={overviewError}
            />
          </div>
        </section>
      )}
    </div>
  );
}